import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, IconButton, CircularProgress, FormControl, InputLabel, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Business as BusinessIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    Info as InfoIcon,
    HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = {
    get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
    post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),
};

const getConfig = (key) => api.get(`/config/${key}`);
const saveConfig = (key, data) => api.post(`/config/${key}`, data);

const CENTROS_CUSTO_KEY = 'centros_custo';
const MAPEAMENTO_CC_KEY = 'mapeamento_cc';

const CentrosDeCusto = () => {
    const location = useLocation();
    const missingCentersFromNav = location.state?.missingCenters || [];

    const [centros, setCentros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Upload CSV & Layout Dialog State
    const [fileSelected, setFileSelected] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [openLayoutDialog, setOpenLayoutDialog] = useState(false);

    // Missing centers from balancete upload
    const [pendingCenters, setPendingCenters] = useState([]);

    // Form state
    const [novoNome, setNovoNome] = useState('');
    const [novoTipo, setNovoTipo] = useState('Apoio');

    // Store all raw missing centers once they are fetched
    const [rawMissingCenters, setRawMissingCenters] = useState([]);
    
    // Mapeamento De-Para
    const [mapeamentoCc, setMapeamentoCc] = useState({});
    const [localMappingSelection, setLocalMappingSelection] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        // Filter out missing centers that are already in the `centros` array or mapped
        const combinedMissing = Array.from(new Set([...missingCentersFromNav, ...rawMissingCenters]));
        if (combinedMissing.length > 0) {
            const filtered = combinedMissing.filter(
                mc => !centros.some(c => c.nome.toUpperCase() === mc.toUpperCase()) && 
                      !(mc in mapeamentoCc)
            );
            setPendingCenters(filtered);
        } else {
            setPendingCenters([]);
        }
    }, [missingCentersFromNav, centros, rawMissingCenters, mapeamentoCc]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [res, faltantesRes, mapRes] = await Promise.all([
                getConfig(CENTROS_CUSTO_KEY),
                api.get('/centros_custo/faltantes'),
                getConfig(MAPEAMENTO_CC_KEY)
            ]);

            if (res.data && Array.isArray(res.data)) {
                setCentros(res.data);
            } else {
                setCentros([]);
            }
            
            if (mapRes.data && typeof mapRes.data === 'object' && !Array.isArray(mapRes.data)) {
                setMapeamentoCc(mapRes.data);
            }

            if (faltantesRes.data && faltantesRes.data.nao_cadastradas) {
                setRawMissingCenters(faltantesRes.data.nao_cadastradas);
            }

        } catch (err) {
            console.error('Erro ao carregar centros de custo:', err);
            setError('Erro ao carregar dados. Verifique a conexão.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCentro = () => {
        if (!novoNome) {
            setError('O nome do centro de custo é obrigatório.');
            return;
        }
        if (centros.some(c => c.nome.toUpperCase() === novoNome.toUpperCase())) {
            setError('Já existe um centro de custo com este nome.');
            return;
        }

        const novoCentro = { nome: novoNome, tipo: novoTipo };
        setCentros([...centros, novoCentro]);
        setNovoNome('');
        setSuccess('Centro de custo adicionado à lista (não salvo ainda).');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleQuickAdd = (centerName, tipo = 'Apoio') => {
        if (centros.some(c => c.nome.toUpperCase() === centerName.toUpperCase())) {
            setError('Este centro de custo já está cadastrado.');
            setTimeout(() => setError(null), 3000);
            return;
        }

        const novoCentro = { nome: centerName, tipo };
        setCentros([...centros, novoCentro]);
        setPendingCenters(pendingCenters.filter(pc => pc !== centerName));
        setSuccess(`'${centerName}' adicionado! Clique em 'Salvar Alterações' para confirmar.`);
        setTimeout(() => setSuccess(null), 3000);
    };
    
    const handleMapCentro = (centerName) => {
        const targetCentro = localMappingSelection[centerName];
        if (!targetCentro) {
            setError('Selecione um centro de custo de destino para mapear.');
            setTimeout(() => setError(null), 3000);
            return;
        }
        
        const novoMapeamento = { ...mapeamentoCc, [centerName]: targetCentro };
        setMapeamentoCc(novoMapeamento);
        setPendingCenters(pendingCenters.filter(pc => pc !== centerName));
        setSuccess(`'${centerName}' mapeado para '${targetCentro}'! Salve as alterações.`);
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleRemoveCentro = (index) => {
        const newCentros = centros.filter((_, i) => i !== index);
        setCentros(newCentros);
    };

    const handleUpdateCentro = (index, field, value) => {
        const newCentros = [...centros];
        newCentros[index][field] = value;
        setCentros(newCentros);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveConfig(CENTROS_CUSTO_KEY, centros);
            await saveConfig(MAPEAMENTO_CC_KEY, mapeamentoCc);
            setSuccess('Centros de custo e mapeamentos salvos com sucesso!');
            setPendingCenters([]); // Clear pending centers after save
        } catch (err) {
            setError('Erro ao salvar no banco de dados.');
        } finally {
            setSaving(false);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const handleFileUpload = async () => {
        if (!fileSelected) {
            setError('Selecione um arquivo CSV para importar.');
            return;
        }
        setUploading(true);
        setError(null);
        setSuccess(null);

        const formData = new FormData();
        formData.append('file', fileSelected);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const res = await axios.post(`${API_URL}/upload_centros_custo`, formData, { headers });
            setSuccess(res.data?.message || 'Centros de custo importados com sucesso!');
            setFileSelected(null);
            await fetchData();
        } catch (err) {
            console.error('Erro ao importar arquivo CSV:', err);
            const errorMsg = err.response?.data?.error || 'Erro ao importar o arquivo CSV. Verifique o formato do arquivo.';
            setError(errorMsg);
        } finally {
            setUploading(false);
            setTimeout(() => setSuccess(null), 5000);
        }
    };

    const handleDownloadTemplate = () => {
        const csvContent = "Nome;Tipo;Descrição\nUTI ADULTO;Produtivo;Unidade de Terapia Intensiva\nCENTRO CIRURGICO;Produtivo;Salas de cirurgia e pós-anestesia\nALMOXARIFADO;Apoio;Controle de estoque e suprimentos\nDIRETORIA;Administrativo;Gestão e administração geral\n";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'centros_custo_modelo.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} /> Gestão de Centros de Custo
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Defina os centros de custo da instituição e classifique-os (Produtivo, Apoio, Administrativo).
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            {/* Importar Centros de Custo via CSV */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudUploadIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Importar Centros de Custo via CSV
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            startIcon={<InfoIcon />}
                            onClick={() => setOpenLayoutDialog(true)}
                        >
                            Layout de Importação
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadTemplate}
                        >
                            Baixar Modelo CSV
                        </Button>
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selecione um arquivo CSV contendo os centros de custo (colunas aceitas: <strong>Nome</strong>, <strong>Tipo</strong> [Produtivo, Apoio, Administrativo], <strong>Descrição</strong>).
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8} md={6}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Selecionar CSV
                                <input
                                    type="file"
                                    hidden
                                    accept=".csv"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setFileSelected(e.target.files[0]);
                                        }
                                    }}
                                />
                            </Button>
                            <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, color: 'text.secondary' }}>
                                {fileSelected ? fileSelected.name : 'Nenhum arquivo selecionado'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleFileUpload}
                            disabled={!fileSelected || uploading}
                            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                            fullWidth
                        >
                            {uploading ? 'Importando...' : 'Importar CSV'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Quick Add Section for Missing Centers */}
            {pendingCenters.length > 0 && (
                <Paper sx={{ p: 3, mb: 4, bgcolor: 'warning.light', border: 2, borderColor: 'warning.main' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'warning.dark' }}>
                        <WarningIcon sx={{ mr: 1 }} />
                        Centros de Custo Pendentes / Não Cadastrados ({pendingCenters.length})
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Estes centros foram encontrados nos lançamentos mas não possuem classificação.
                        Você pode adicioná-los como um novo centro de custo ou mapeá-los (De-Para) para um centro já existente.
                    </Alert>
                    <Grid container spacing={2}>
                        {pendingCenters.map((center, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'background.paper' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        {center}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleQuickAdd(center, 'Apoio')}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            Novo CC
                                        </Button>
                                        
                                        <Typography variant="body2" sx={{ mx: 1, color: 'text.secondary' }}>ou</Typography>
                                        
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Mapear para...</InputLabel>
                                            <Select
                                                label="Mapear para..."
                                                value={localMappingSelection[center] || ''}
                                                onChange={(e) => setLocalMappingSelection({...localMappingSelection, [center]: e.target.value})}
                                            >
                                                {centros.map((c, i) => (
                                                    <MenuItem key={i} value={c.nome}>{c.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleMapCentro(center)}
                                            disabled={!localMappingSelection[center]}
                                        >
                                            Mapear
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}

            <Grid container spacing={4}>
                {/* Form de Adição */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Novo Centro de Custo</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Nome do Centro"
                                value={novoNome}
                                onChange={(e) => setNovoNome(e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="Ex: UTI Adulto"
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                    value={novoTipo}
                                    label="Tipo"
                                    onChange={(e) => setNovoTipo(e.target.value)}
                                >
                                    <MenuItem value="Produtivo">Produtivo</MenuItem>
                                    <MenuItem value="Apoio">Apoio</MenuItem>
                                    <MenuItem value="Administrativo">Administrativo</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddCentro}
                            >
                                Adicionar
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Lista */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Centros Cadastrados ({centros.length})</Typography>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        </Box>

                        <TableContainer sx={{ maxHeight: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {centros.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                Nenhum centro de custo cadastrado.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        centros.map((centro, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    <TextField
                                                        value={centro.nome}
                                                        onChange={(e) => handleUpdateCentro(index, 'nome', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        variant="standard"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={centro.tipo || 'Apoio'}
                                                        onChange={(e) => handleUpdateCentro(index, 'tipo', e.target.value)}
                                                        size="small"
                                                        variant="standard"
                                                        fullWidth
                                                    >
                                                        <MenuItem value="Produtivo">Produtivo</MenuItem>
                                                        <MenuItem value="Apoio">Apoio</MenuItem>
                                                        <MenuItem value="Administrativo">Administrativo</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleRemoveCentro(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Modal de Instruções do Layout de Importação */}
            <Dialog open={openLayoutDialog} onClose={() => setOpenLayoutDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                    <InfoIcon color="info" /> Layout do Arquivo de Importação (CSV)
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" paragraph>
                        O arquivo para importação de <strong>Centros de Custo</strong> deve estar no formato CSV (separado por ponto e vírgula <code>;</code> ou vírgula <code>,</code>) com codificação <strong>UTF-8</strong>.
                    </Typography>
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                        Colunas Aceitas no Cabeçalho:
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Coluna</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Obrigatoriedade</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Valores / Descrição</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>Nome</TableCell>
                                    <TableCell><Chip label="Obrigatório" color="error" size="small" variant="outlined" /></TableCell>
                                    <TableCell>Nome do centro de custo (ex: <code>UTI ADULTO</code>). Aceita também: <code>Centro de Custo</code>, <code>Centro</code>.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>Tipo</TableCell>
                                    <TableCell><Chip label="Opcional" color="default" size="small" variant="outlined" /></TableCell>
                                    <TableCell>Classificação: <code>Produtivo</code>, <code>Apoio</code> ou <code>Administrativo</code> (Padrão: Apoio).</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>Descrição</TableCell>
                                    <TableCell><Chip label="Opcional" color="default" size="small" variant="outlined" /></TableCell>
                                    <TableCell>Descrição complementar ou detalhes do centro de custo.</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Exemplo de Conteúdo CSV:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.900', color: 'common.white', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        Nome;Tipo;Descrição<br />
                        UTI ADULTO;Produtivo;Unidade de Terapia Intensiva<br />
                        CENTRO CIRURGICO;Produtivo;Salas de cirurgia<br />
                        ALMOXARIFADO;Apoio;Suprimentos e Estoque<br />
                        DIRETORIA;Administrativo;Gestão Geral
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} color="primary">
                        Baixar Arquivo Modelo
                    </Button>
                    <Button onClick={() => setOpenLayoutDialog(false)} variant="contained">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CentrosDeCusto;